import axios from 'axios';
import { message } from 'antd';
import { useUserStore } from '@/stores/userStore';

export interface DatabaseGrant {
  grant_id: string;
  resource_id: string;
  database: { host: string; port: number; db_name: string | null; name?: string; username: string };
  tables: { db_name: string; table_name: string }[];
}

export interface ServerGrant {
  grant_id: string;
  resource_id: string;
  server: { host: string; port: number; ssh_username: string; name?: string };
}

export interface CredentialNotification {
  sent: boolean;
  email?: string;
  sent_count: number;
  error?: string | null;
}

const apiClient = axios.create({
  baseURL: '/api/v1',
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const userInfo = useUserStore.getState().userInfo;
  if (userInfo?.id) {
    config.headers = config.headers || {};
    (config.headers as any)['X-User-Identifier'] = userInfo.id;
  }
  return config;
});

apiClient.interceptors.response.use(
  (resp) => resp,
  (error) => {
    if ((error?.response?.status ?? 0) >= 500) {
      message.error('宝箱服务暂时不可用，请稍后再试');
    }
    return Promise.reject(error);
  },
);

export const treasureGetDatabase = (): Promise<DatabaseGrant[]> =>
  apiClient.get<DatabaseGrant[]>('/me/treasure/database').then((r) => r.data);

export const treasureGetServer = (): Promise<ServerGrant[]> =>
  apiClient.get<ServerGrant[]>('/me/treasure/server').then((r) => r.data);

export const treasureSendDatabaseCredentials = (): Promise<CredentialNotification> =>
  apiClient.post<CredentialNotification>('/me/treasure/send-credentials/database').then((r) => r.data);

export const treasureSendServerCredentials = (): Promise<CredentialNotification> =>
  apiClient.post<CredentialNotification>('/me/treasure/send-credentials/server').then((r) => r.data);

export interface PrivatePassword {
  id: string;
  name: string;
  serviceUrl: string;
  username: string;
  password: string;
  tags: string[];
  remark: string;
  createdAt: string;
  updatedAt: string;
}

export type PrivatePasswordInput = Partial<Omit<PrivatePassword, 'id' | 'createdAt' | 'updatedAt'>>;

export const passwordsGet = (): Promise<PrivatePassword[]> =>
  apiClient.get<PrivatePassword[]>('/me/treasure/passwords').then((r) => r.data);

export const passwordCreate = (data: PrivatePasswordInput): Promise<PrivatePassword> =>
  apiClient.post<PrivatePassword>('/me/treasure/passwords', data).then((r) => r.data);

export const passwordUpdate = (id: string, data: PrivatePasswordInput): Promise<PrivatePassword> =>
  apiClient.patch<PrivatePassword>(`/me/treasure/passwords/${id}`, data).then((r) => r.data);

export const passwordDelete = (id: string): Promise<void> =>
  apiClient.delete(`/me/treasure/passwords/${id}`).then(() => undefined);

export const passwordSendCredentials = (): Promise<CredentialNotification> =>
  apiClient.post<CredentialNotification>('/me/treasure/send-credentials/passwords').then((r) => r.data);

export interface ServiceSecretFields {
  serviceName: string;
  requestPublicUrl: string;
  requestInternalUrl: string;
  groupName: string;
  tenantName: string;
  applicationName: string;
  appId: string;
  clientId: string;
}

export interface ServiceSecretValues {
  accessKey: string;
  secretKey: string;
  personalToken: string;
  apiKey: string;
}

export interface ServiceSecretSummary extends ServiceSecretFields {
  id: string;
  hasAccessKey: boolean;
  hasSecretKey: boolean;
  hasPersonalToken: boolean;
  hasApiKey: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceSecretDetail extends ServiceSecretFields, ServiceSecretValues {
  id: string;
  createdAt: string;
  updatedAt: string;
}

export type ServiceSecretInput = Partial<ServiceSecretFields & ServiceSecretValues>;

export const serviceSecretsGet = (): Promise<ServiceSecretSummary[]> =>
  apiClient.get<ServiceSecretSummary[]>('/me/treasure/service-secrets').then((r) => r.data);

export const serviceSecretCreate = (data: ServiceSecretInput): Promise<ServiceSecretSummary> =>
  apiClient.post<ServiceSecretSummary>('/me/treasure/service-secrets', data).then((r) => r.data);

export const serviceSecretUpdate = (
  id: string,
  data: ServiceSecretInput,
): Promise<ServiceSecretSummary> =>
  apiClient.patch<ServiceSecretSummary>(`/me/treasure/service-secrets/${id}`, data).then((r) => r.data);

export const serviceSecretDelete = (id: string): Promise<void> =>
  apiClient.delete(`/me/treasure/service-secrets/${id}`).then(() => undefined);

export const serviceSecretReveal = (
  id: string,
  password: string,
): Promise<ServiceSecretDetail> =>
  apiClient
    .post<ServiceSecretDetail>(`/me/treasure/service-secrets/${id}/reveal`, { password })
    .then((r) => r.data);
